/**
 * Password validation utility that uses the server's password policy.
 * This ensures frontend validation always matches backend requirements.
 */

export interface PasswordPolicy {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_digit: boolean;
  require_special_char: boolean;
  special_chars: string;
  forbidden_patterns: string[];
}

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validates a password against the provided policy.
 * Returns an array of validation errors (empty if valid).
 */
export function validatePassword(
  password: string,
  policy: PasswordPolicy
): string[] {
  const errors: string[] = [];

  // Check minimum length
  if (password.length < policy.min_length) {
    errors.push(`Password must be at least ${policy.min_length} characters long`);
  }

  // Check for uppercase letter
  if (policy.require_uppercase && !/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  // Check for lowercase letter
  if (policy.require_lowercase && !/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  // Check for digit
  if (policy.require_digit && !/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  // Check for special character
  if (policy.require_special_char) {
    const specialChars = policy.special_chars || "!@#$%^&*(),.?\":{}|<>_-+=[]\\/~`";
    const hasSpecial = specialChars.split('').some(char => password.includes(char));
    if (!hasSpecial) {
      errors.push(`Password must contain at least one special character (${specialChars})`);
    }
  }

  // Check for forbidden patterns
  if (policy.forbidden_patterns && policy.forbidden_patterns.length > 0) {
    const lowerPassword = password.toLowerCase();
    for (const pattern of policy.forbidden_patterns) {
      if (lowerPassword.includes(pattern.toLowerCase())) {
        errors.push("Password contains a common pattern. Please choose a stronger password.");
        break;
      }
    }
  }

  return errors;
}

/**
 * Calculates password strength based on policy requirements.
 * Returns { score: 0-5, label: string, color: string }
 */
export function calculatePasswordStrength(password: string, policy: PasswordPolicy): {
  score: number;
  label: string;
  color: string;
} {
  if (!password) {
    return { score: 0, label: "", color: "#ccc" };
  }

  let score = 0;

  // Length score
  if (password.length >= policy.min_length) score++;
  if (password.length >= policy.min_length + 4) score++;

  // Complexity score
  if (!policy.require_uppercase || /[A-Z]/.test(password)) score++;
  if (!policy.require_lowercase || /[a-z]/.test(password)) score++;
  if (!policy.require_digit || /[0-9]/.test(password)) score++;

  // Special char and no forbidden patterns
  const specialChars = policy.special_chars || "!@#$%^&*(),.?\":{}|<>_-+=[]\\/~`";
  const hasSpecial = specialChars.split('').some(char => password.includes(char));
  if (!policy.require_special_char || hasSpecial) score++;

  // Check for forbidden patterns (reduce score)
  if (policy.forbidden_patterns && policy.forbidden_patterns.length > 0) {
    const lowerPassword = password.toLowerCase();
    for (const pattern of policy.forbidden_patterns) {
      if (lowerPassword.includes(pattern.toLowerCase())) {
        score = Math.max(0, score - 2);
        break;
      }
    }
  }

  // Map score to label and color
  if (score <= 2) return { score, label: "Weak", color: "#dc3545" };
  if (score <= 4) return { score, label: "Medium", color: "#ffc107" };
  return { score, label: "Strong", color: "#28a745" };
}
